import {
  CalendarIcon,
  ClipboardDocumentCheckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import { tasksApiClient } from '@/services/api/tasksService';
import type { Task } from '@/services/types/client-intake-types';
import { formatTaskDate } from '@/utils/taskDateUtils';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { UserLink } from './UserLink';

// ============================================================================
// Tooltip Content
// ============================================================================

const TaskTooltipContent = memo<EntityTooltipContentProps<Task>>(
  ({ fetchState }) => {
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="task" />;
    }

    if (fetchState.status === 'idle' || !fetchState.data) {
      return <EmptyState entityType="task" />;
    }

    const task = fetchState.data;
    const { name, type, status, due_date, assignee } = task;

    // Handle nested objects for type and status if they are objects
    const typeName = typeof type === 'object' ? type.name : type;
    const statusName = typeof status === 'object' ? status.name : status;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader
          icon={ClipboardDocumentCheckIcon}
          title={name || typeName || 'Task'}
        />

        <TooltipSection withBorder>
          <div className="mb-2 flex items-center justify-between gap-2">
            {statusName && createStatusBadge({ value: statusName })}
            {typeName && (
              <span className="text-[10px] font-medium text-gray-400">
                {typeName}
              </span>
            )}
          </div>

          {due_date && (
            <InfoRow
              icon={CalendarIcon}
              label="Due Date"
              value={formatTaskDate(due_date)}
            />
          )}
          {assignee && (
            <InfoRow
              icon={UserIcon}
              label="Assigned to"
              value={
                <UserLink entityId={assignee.id} side="left">
                  {assignee.first_name} {assignee.last_name}
                </UserLink>
              }
            />
          )}
        </TooltipSection>
      </div>
    );
  },
);

TaskTooltipContent.displayName = 'TaskTooltipContent';

// ============================================================================
// Component
// ============================================================================

export const TaskLink = createEntityLink<
  Task,
  { matterId: string; taskId: string },
  { matterId: string }
>({
  tooltipContent: TaskTooltipContent,
  getDetailUrl: (id, { matterId }) => ({
    pathname: `/matters/${matterId}/`,
    search: `?task_id=${id}`,
  }),
  useQuery: tasksApiClient.endpoints.getTask.useQuery,
  getQueryArg: (id, { matterId }) => ({ matterId, taskId: id }),
  displayName: 'TaskLink',
});
